import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import type { AddressType } from '@sg/garnish';
import { AddressOrder, AddressTypes, BodyText, theme } from '@sg/garnish';
import type { CourierDetails } from '@sg/graphql-schema';
import type { Ledger } from '@sg/graphql-schema';
import { DeliveryPreferenceType, type FlattenedOrderStatuses } from '@sg/graphql-schema';

import { usePreferencesText } from '../../../../components';
import { type PartialLineItem } from '../../../../graphql';

export const OrderStatusAddress = (props: OrderStatusDetailsProps) => {
  const {
    orderType,
    deliveryAddressName,
    deliveryAddressStreet,
    deliveryAddressSecondaryStreet,
    deliveryAddressCity,
    deliveryAddressState,
    deliveryAddressZipCode,
    deliveryAddressNotes,
    deliveryPreference,
    locationName,
    locationAddress,
    locationCity,
    locationState,
    locationZipCode,
    dropoffLocation,
  } = props;

  const deliveryPreferenceText = usePreferencesText(
    deliveryPreference ?? DeliveryPreferenceType.LeaveAtDoor,
  );
  const isDelivery = orderType === 'delivery';

  return (
    // @ts-expect-error TS(2769): No overload matches this call.
    <View dataSet={{ ddPrivacy: isDelivery ? 'mask' : 'mask-user-input' }}>
      <BodyText sizeMatch={['16']} bold style={styles.addressHeader}>
        {useAddressTitle(orderType)}
      </BodyText>

      <AddressOrder
        addressType={orderType}
        name={isDelivery ? deliveryAddressName : locationName}
        street={isDelivery ? deliveryAddressStreet : locationAddress}
        city={isDelivery ? deliveryAddressCity : locationCity}
        state={isDelivery ? deliveryAddressState : locationState}
        zipCode={isDelivery ? deliveryAddressZipCode : locationZipCode}
        secondaryStreet={deliveryAddressSecondaryStreet}
        hideIcon
      />

      {dropoffLocation ? (
        <View style={styles.preferences}>
          <BodyText size={4}>{dropoffLocation}</BodyText>
        </View>
      ) : null}

      {isDelivery ? (
        <View style={styles.preferences}>
          <BodyText size={4}>{deliveryPreferenceText}</BodyText>

          <BodyText size={4}>{deliveryAddressNotes}</BodyText>
        </View>
      ) : null}
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const useAddressTitle = (orderType: AddressType) => {
  const { formatMessage } = useIntl();

  if (orderType === AddressTypes.pickup) {
    return formatMessage(messages.pickupLocationTitle);
  }

  if (orderType === AddressTypes.delivery) {
    return formatMessage(messages.deliveryLocationTitle);
  }

  if (orderType === AddressTypes.outpost) {
    return formatMessage(messages.outpostLocationTitle);
  }

  return formatMessage(messages.inStoreLocationTitle);
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  pickupLocationTitle: {
    defaultMessage: 'Pickup location',
    description: 'Order Status | Location | Title Pickup',
  },
  deliveryLocationTitle: {
    defaultMessage: 'Delivery location',
    description: 'Order Status | Location | Title Delivery',
  },
  outpostLocationTitle: {
    defaultMessage: 'Outpost location',
    description: 'Order Status | Location | Title Outpost',
  },
  inStoreLocationTitle: {
    defaultMessage: 'Store location',
    description: 'Order Status | Location | Title In-store',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  addressHeader: {
    marginBottom: theme.spacing['4'],
  },
  preferences: {
    marginTop: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderStatusDetailsProps = Readonly<{
  orderType: AddressType;
  orderStatus?: FlattenedOrderStatuses;
  deliveryAddressName?: string;
  deliveryAddressStreet?: string;
  deliveryAddressSecondaryStreet?: string;
  deliveryAddressCity?: string;
  deliveryAddressState?: string;
  deliveryAddressZipCode?: string;
  deliveryAddressNotes?: string;
  deliveryPreference?: DeliveryPreferenceType;
  locationName?: string;
  locationAddress?: string;
  locationCity?: string;
  locationState?: string;
  locationZipCode?: string;
  lineItems: readonly PartialLineItem[];
  dropoffLocation?: string;
  ledger: Partial<Ledger>;
  courierName?: CourierDetails['name'];
  courierPhone?: CourierDetails['phone'];
}>;
